import { mutationOptions, queryOptions } from "@tanstack/react-query";
import { z } from "zod";
import type { Answer, AnswerRequest, Form, QuestionCard } from "../dynamicForm";
import type { AnswerSnapshot, CustomHistoryEntry, HistoryEntry } from "../dynamicHistory/types";
import { api } from "./apiClient";
import { FORMS_API_BASE_ENDPOINT } from "./constants";
import { invalidateAll } from "./invalidation";
import { queryClient } from "./queryClient";

const BASE_ENDPOINT = `${FORMS_API_BASE_ENDPOINT}/partners`;

const partnerKeys = {
  all: () => ["partners"] as const,
  byId: (id: string) => [...partnerKeys.all(), id] as const,
  gridView: () => [...partnerKeys.all(), "gridview"] as const,
  form: () => [...partnerKeys.all(), "form"] as const,
  selectOptions: () => [...partnerKeys.all(), "select-options"] as const,
  complianceForm: () => [...partnerKeys.all(), "complianceform"] as const,
  complianceById: (id: string) => [...partnerKeys.all(), "compliance", id] as const,
  complianceHistory: (id: string) => [...partnerKeys.all(), "compliance", id, "history"] as const,
} as const;

const AGREEMENT_TYPES = [
  "processorAgreementPartnerActsAsProcessor",
  "processorAgreementWeActAsProcessor",
  "subProcessorAgreementWeActAsProcessor",
  "systemProviderAgreement",
  "jointControllerAgreement",
  "recipientAgreement",
] as const;

export const PARTNER_ROLES = [
  "processor",
  "controllerOurClient",
  "organisationWeUseAsSubProcessor",
  "systemProvider",
  "jointController",
  "recipient",
] as const;

const PARTNER_LIFECYCLE_STATUSES = ["inactive", "active", "archived"] as const;

export type PartnerLifecycleStatus = (typeof PARTNER_LIFECYCLE_STATUSES)[number];

export type PartnerRole = (typeof PARTNER_ROLES)[number];

export const EvaluationSchema = z.enum(["approved", "approvedWithConditions", "rejected"]);

const GetPartnersGridViewResponseSchema = z.object({
  partners: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      answers: z.any() as z.ZodType<Answer[]>,
      roles: z.array(z.enum(PARTNER_ROLES)),
      status: z.enum(PARTNER_LIFECYCLE_STATUSES),
      compliance: z.object({
        id: z.string(),
        overallAssessment: EvaluationSchema.optional(),
      }),
    })
  ),
});

export type GetPartnersGridViewResponse = z.infer<typeof GetPartnersGridViewResponseSchema>;

export const partnersGridViewQueryOptions = () =>
  queryOptions({
    queryKey: partnerKeys.gridView(),
    queryFn: () => api.get(`${BASE_ENDPOINT}/gridview`, { schema: GetPartnersGridViewResponseSchema }),
    throwOnError: true,
  });

export const partnerFormQueryOptions = () =>
  queryOptions({
    queryKey: partnerKeys.form(),
    queryFn: () => api.get(`${BASE_ENDPOINT}/form`) as Promise<Form>,
    throwOnError: true,
  });

const ROLE_CONNECTED_ENTITIES_FORM_TYPES = ["privacyRegistration", "privacyProcessorRegistration", "system"] as const;
export type RoleConnectedEntitiesFormType = (typeof ROLE_CONNECTED_ENTITIES_FORM_TYPES)[number];

const ComplianceSectionSchema = z.object({
  id: z.string(),
  name: z.string(),
  questionCards: z.any() as z.ZodType<QuestionCard[]>,
});

const ComplianceFormSchema = z.object({
  id: z.string(),
  name: z.string(),
  sections: z.array(ComplianceSectionSchema),
});

export type Evaluation = z.infer<typeof EvaluationSchema>;

const SectionEvaluationSchema = z.object({
  sectionId: z.string(),
  assessment: EvaluationSchema.nullish(),
  comment: z.string().nullish(),
});

export type SectionEvaluation = z.infer<typeof SectionEvaluationSchema>;

const UserSchema = z.object({
  userId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
});

const ComplianceWithAnswersSchema = z
  .object({
    id: z.string(),
    partnerId: z.string(),
    answers: z.any() as z.ZodType<Answer[]>,
    evaluation: z.object({
      comment: z.string().nullish(),
      sectionEvaluations: z.array(SectionEvaluationSchema),
      assessment: EvaluationSchema.optional(),
      reviewed: z.object({ by: UserSchema, at: z.string() }).optional(),
    }),
    requestedAt: z.string().optional(),
    nextAudit: z.string().optional(),
    reviewIntervalMonths: z.number(),
  })
  .and(
    z.discriminatedUnion("externalAssessmentRequestStatus", [
      z.object({
        externalAssessmentRequestStatus: z.literal("awaiting-response"),
        requestedAt: z.string(),
        requestedEmail: z.string(),
      }),
      z.object({ externalAssessmentRequestStatus: z.literal("idle") }),
    ])
  )
  .and(
    z.union([
      z.object({
        manual: z.undefined().optional(),
        external: z.object({
          answeredAt: z.string(),
          answeredBy: UserSchema,
          answerSnapshotsChangedSinceReviewedAt: z.array(
            z.object({ questionCardId: z.string(), snapshot: z.custom<AnswerSnapshot>() })
          ),
        }),
      }),
      z.object({ external: z.undefined().optional(), manual: z.object({ answerUpdatedAt: z.string().optional() }) }),
    ])
  );

export type ComplianceWithAnswers = z.infer<typeof ComplianceWithAnswersSchema>;
export type ComplianceSection = z.infer<typeof ComplianceSectionSchema>;

const RoleActiveStatusSchema = z.enum(["active", "inactive"]);
export type RoleActiveStatus = z.infer<typeof RoleActiveStatusSchema>;

const ContactPersonValueSchema = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string().optional(),
  phoneNumber: z.string().optional(),
  title: z.string().optional(),
});

type ContactPerson = z.infer<typeof ContactPersonValueSchema>;

const AgreementSchema = z.object({
  id: z.string(),
  title: z.string(),
  url: z.string(),
  validUntil: z.string().optional(),
  type: z.enum(AGREEMENT_TYPES),
});
export type Agreement = z.infer<typeof AgreementSchema>;

const GetPartnerByIdResponseSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: z.enum(PARTNER_LIFECYCLE_STATUSES),
  totalConnectedEntitiesCount: z.number(),
  answers: z.any() as z.ZodType<Answer[]>,
  partnerSecurityAndPrivacyProfileEnabled: z.boolean(),
  agreementDocuments: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      url: z.string(),
      type: z.enum(AGREEMENT_TYPES),
      validUntil: z.string().optional(),
    })
  ),
  rolesAndRelationships: z.array(
    z.object({
      activeStatus: RoleActiveStatusSchema,
      role: z.enum(PARTNER_ROLES),
      formType: z.enum(ROLE_CONNECTED_ENTITIES_FORM_TYPES),
      connectedEntities: z.array(z.object({ responseId: z.string(), responseName: z.string() })),
      description: z.string().nullish(),
      contactPerson: ContactPersonValueSchema.nullish(),
      agreementType: z.enum(AGREEMENT_TYPES),
      agreementDocuments: z.array(AgreementSchema),
      totalConnectedEntitiesCount: z.number(),
    })
  ),
  compliance: z.object({
    id: z.string(),
  }),
});
export type GetPartnerByIdResponse = z.infer<typeof GetPartnerByIdResponseSchema>;

export const partnerByIdQueryOptions = (id: string) =>
  queryOptions({
    queryKey: partnerKeys.byId(id),
    queryFn: () => api.get(`${BASE_ENDPOINT}/${id}`, { schema: GetPartnerByIdResponseSchema }),
    throwOnError: true,
  });

const CreatePartnerResponseSchema = z.object({ id: z.string() });
export const CreatePartnerErrorResponseSchema = z.object({ errorCode: z.enum(["PARTNER_ALREADY_EXISTS"]) });
export type CreatePartnerPayload = { name: string };

export const createPartnerMutationOptions = () =>
  mutationOptions({
    mutationFn: async (payload: CreatePartnerPayload) =>
      await api.post(BASE_ENDPOINT, payload, { schema: CreatePartnerResponseSchema }),
    onSuccess: () => queryClient.invalidateQueries(),
  });

export type UpdatePartnerPayload = z.infer<typeof UpdatePartnerPayloadSchema>;
const UpdatePartnerPayloadSchema = z.object({ name: z.string().min(1) });

export const updatePartnerMutationOptions = () =>
  mutationOptions({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdatePartnerPayload }) =>
      await api.patch(`${BASE_ENDPOINT}/${id}`, payload, { schema: UpdatePartnerPayloadSchema }),
    onSuccess: () => queryClient.invalidateQueries(),
  });

export const deletePartnerMutationOptions = () =>
  mutationOptions({
    mutationFn: (id: string) => api.delete(`${BASE_ENDPOINT}/${id}`),
    onSuccess: (_, id) => invalidateAll({ ignore: [partnerKeys.byId(id)] }),
  });

export const updatePartnerAnswersMutationOptions = () =>
  mutationOptions({
    mutationFn: ({ partnerId, answers }: { partnerId: string; answers: AnswerRequest[] }) =>
      api.patch(`${BASE_ENDPOINT}/${partnerId}/answers`, answers),
    onSuccess: () => queryClient.invalidateQueries(),
  });

export type CreateOrUpdateAgreementPayload = {
  title: string;
  url: string;
  type: (typeof AGREEMENT_TYPES)[number];
  validUntil?: string;
};
const CreateAgreementResponseSchema = z.object({ id: z.string() });

export const createOrUpdateAgreementMutationOptions = ({
  partnerId,
  agreementId,
}: {
  partnerId: string;
  agreementId?: string;
}) => {
  return mutationOptions({
    mutationFn: async (payload: CreateOrUpdateAgreementPayload) => {
      const ENDPOINT = `${BASE_ENDPOINT}/${partnerId}/agreements${agreementId ? `/${agreementId}` : ""}`;
      return CreateAgreementResponseSchema.parse(
        agreementId ? await api.patch(ENDPOINT, payload) : await api.post(ENDPOINT, payload)
      );
    },
    onSuccess: () => queryClient.invalidateQueries(),
  });
};

export const deleteAgreementMutationOptions = ({ partnerId }: { partnerId: string }) =>
  mutationOptions({
    mutationFn: (agreementId: string) => api.delete(`${BASE_ENDPOINT}/${partnerId}/agreements/${agreementId}`),
    onSuccess: () => queryClient.invalidateQueries(),
  });

const GetSelectPartnerOptionsResponseSchema = z.array(
  z.object({ id: z.string(), name: z.string(), roles: z.array(z.enum(PARTNER_ROLES)) })
);

export const addPartnerRoleMutationOptions = ({ partnerId }: { partnerId: string }) =>
  mutationOptions({
    mutationFn: (payload: { role: PartnerRole }) => api.post(`${BASE_ENDPOINT}/${partnerId}/roles`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries();
      return queryClient.invalidateQueries({ queryKey: partnerKeys.byId(partnerId) });
    },
  });

export const deletePartnerRoleMutationOptions = ({ partnerId }: { partnerId: string }) =>
  mutationOptions({
    mutationFn: (role: PartnerRole) => api.delete(`${BASE_ENDPOINT}/${partnerId}/roles/${role}`),
    onSuccess: () => queryClient.invalidateQueries(),
  });

export const updatePartnerRoleMutationOptions = ({ partnerId, role }: { partnerId: string; role: PartnerRole }) =>
  mutationOptions({
    mutationFn: (payload: { description?: string; contactPerson?: ContactPerson }) =>
      api.patch(`${BASE_ENDPOINT}/${partnerId}/roles/${role}`, payload),
    onSuccess: () => queryClient.invalidateQueries(),
  });

export const deletePartnerRoleContactPersonMutationOptions = ({
  partnerId,
  role,
}: {
  partnerId: string;
  role: PartnerRole;
}) =>
  mutationOptions({
    mutationFn: () => api.delete(`${BASE_ENDPOINT}/${partnerId}/roles/${role}/contact-person`),
    onSuccess: () => queryClient.invalidateQueries(),
  });

export type GetSelectPartnerOptionsResponse = z.infer<typeof GetSelectPartnerOptionsResponseSchema>;

export const selectPartnerOptionsQueryOptions = () =>
  queryOptions({
    queryKey: partnerKeys.selectOptions(),
    queryFn: () => api.get(`${BASE_ENDPOINT}/select-options`, { schema: GetSelectPartnerOptionsResponseSchema }),
    throwOnError: true,
  });

const COMPLIANCE_API_BASE_ENDPOINT = `${FORMS_API_BASE_ENDPOINT}/PartnerCompliance`;
export const getFormByComplianceQueryOptions = () =>
  queryOptions({
    queryKey: partnerKeys.complianceForm(),
    queryFn: () => api.get(`${COMPLIANCE_API_BASE_ENDPOINT}/form`, { schema: ComplianceFormSchema }),
    throwOnError: true,
  });

export const complianceByIdQueryOptions = (complianceId: string) =>
  queryOptions({
    queryKey: partnerKeys.complianceById(complianceId),
    queryFn: () => api.get(`${COMPLIANCE_API_BASE_ENDPOINT}/${complianceId}`, { schema: ComplianceWithAnswersSchema }),
    throwOnError: true,
  });

export type OverallAssessmentHistoryValue = Evaluation | null;
export type OverallAssessmentCommentHistoryValue = string | null;
export type SectionEvaluationHistoryValue = { assessment: Evaluation | null; comment: string | null } | undefined;

type ComplianceHistory = HistoryEntry<
  | CustomHistoryEntry<"overallAssessment", OverallAssessmentHistoryValue>
  | CustomHistoryEntry<"overallComment", OverallAssessmentCommentHistoryValue>
  | (CustomHistoryEntry<"sectionEvaluation", SectionEvaluationHistoryValue> & { sectionHeader: string })
  | CustomHistoryEntry<"partnerComplianceAssessmentResponded">
>;

const complianceHistoryResponseSchema = z.object({ history: z.array(z.custom<ComplianceHistory>()) });

export const complianceHistoryQueryOptions = (complianceId?: string) => {
  if (!complianceId) throw new Error("Compliance ID is required");

  return queryOptions({
    queryKey: partnerKeys.complianceHistory(complianceId),
    queryFn: () =>
      api.get(`${COMPLIANCE_API_BASE_ENDPOINT}/${complianceId}/history`, { schema: complianceHistoryResponseSchema }),
    throwOnError: true,
  });
};

export const updateComplianceAnswersMutationOptions = () =>
  mutationOptions({
    mutationFn: ({ complianceId, answers }: { complianceId: string; answers: AnswerRequest[] }) =>
      api.patch(`${COMPLIANCE_API_BASE_ENDPOINT}/${complianceId}/answers`, answers),
    onSuccess: () => queryClient.invalidateQueries(),
  });

export const UpdateSectionEvaluationPayloadSchema = SectionEvaluationSchema.omit({ sectionId: true });
export type UpdateSectionEvaluationPayload = z.infer<typeof UpdateSectionEvaluationPayloadSchema>;

export const updateSectionEvaluationMutationOptions = ({
  complianceId,
  sectionId,
}: {
  complianceId: string;
  sectionId: string;
}) =>
  mutationOptions({
    mutationFn: (payload: UpdateSectionEvaluationPayload) =>
      api.put(`${COMPLIANCE_API_BASE_ENDPOINT}/${complianceId}/evaluation/${sectionId}`, payload),
    onSuccess: () => queryClient.invalidateQueries(),
  });

export const createOrUpdateEvaluationCommentMutationOptions = ({ complianceId }: { complianceId: string }) =>
  mutationOptions({
    mutationFn: (payload: { comment: string }) =>
      api.post(`${COMPLIANCE_API_BASE_ENDPOINT}/${complianceId}/evaluation/comment`, payload),
    onSuccess: () => queryClient.invalidateQueries(),
  });

export const markPartnerComplianceAsDoneMutationOptions = ({ complianceId }: { complianceId: string }) =>
  mutationOptions({
    mutationFn: (payload: { overallAssessment: Evaluation }) =>
      api.post(`${COMPLIANCE_API_BASE_ENDPOINT}/${complianceId}/mark-as-done`, payload),
    onSuccess: () => queryClient.invalidateQueries(),
  });

export const COMPLIANCE_REVIEW_INTERVAL_MONTHS = [3, 6, 12, 18, 24] as const;
type ComplianceReviewIntervalMonths = (typeof COMPLIANCE_REVIEW_INTERVAL_MONTHS)[number];

export const updateComplianceReviewIntervalMutationOptions = ({ complianceId }: { complianceId: string }) =>
  mutationOptions({
    mutationFn: (payload: { reviewIntervalMonths: ComplianceReviewIntervalMonths }) =>
      api.put(`${COMPLIANCE_API_BASE_ENDPOINT}/${complianceId}/review-interval`, payload),
    onSuccess: () => queryClient.invalidateQueries(),
  });

export const requestPartnerAssessmentMutationOptions = ({ complianceId }: { complianceId: string }) =>
  mutationOptions({
    mutationFn: (payload: { to?: string; subject: string; message: string }) =>
      api.post(
        `${COMPLIANCE_API_BASE_ENDPOINT}/${complianceId}/${payload.to ? "request-partner-assessment" : "send-reminder"}`,
        payload
      ),
    onSuccess: () => queryClient.invalidateQueries(),
  });

export const withdrawAssessmentRequestMutationOptions = ({ complianceId }: { complianceId: string }) =>
  mutationOptions({
    mutationFn: () => api.post(`${COMPLIANCE_API_BASE_ENDPOINT}/${complianceId}/withdraw-assessment-request`),
    onSuccess: () => queryClient.invalidateQueries(),
  });

export const requestPartnerManualEntryMutationOptions = ({ complianceId }: { complianceId: string }) =>
  mutationOptions({
    mutationFn: () => api.post(`${COMPLIANCE_API_BASE_ENDPOINT}/${complianceId}/manual-entry`),
    onSuccess: () => queryClient.invalidateQueries(),
  });

export type MovePartnerConnectedEntitiesPayload = {
  newPartnerId: string;
  connections: Array<{
    responseId: string;
    formType: RoleConnectedEntitiesFormType;
    role: PartnerRole;
  }>;
};

export const movePartnerConnectedEntitiesMutationOptions = (partnerId: string) =>
  mutationOptions({
    mutationFn: (payload: MovePartnerConnectedEntitiesPayload) =>
      api.post(`${BASE_ENDPOINT}/${partnerId}/move-connections`, payload),
    onSuccess: () => queryClient.invalidateQueries(),
  });

export const archivePartnerMutationOptions = (partnerId: string) =>
  mutationOptions({
    mutationFn: () => api.patch(`${BASE_ENDPOINT}/${partnerId}/archive`, {}),
    onSuccess: () => queryClient.invalidateQueries(),
  });

export const unarchivePartnerMutationOptions = (partnerId: string) =>
  mutationOptions({
    mutationFn: () => api.patch(`${BASE_ENDPOINT}/${partnerId}/unarchive`, {}),
    onSuccess: () => queryClient.invalidateQueries(),
  });
export const requestSecurityTrialMutationOptions = () =>
  mutationOptions({
    mutationFn: () => api.post(`${COMPLIANCE_API_BASE_ENDPOINT}/request-security-trial`, {}),
    onSuccess: () => queryClient.invalidateQueries(),
  });
